/**
 * Eigene Cloudflare-Turnstile-Integration.
 *
 * Turnstile wird nicht mehr per Cookie-Consent blockiert. Stattdessen fängt
 * dieses Modul den Submit der in den Settings ausgewählten Formulare ab und
 * zeigt zunächst ein Modal, das auf die notwendige Verbindung zu Cloudflare
 * hinweist. Erst nach Bestätigung wird api.js von challenges.cloudflare.com
 * nachgeladen, das Widget gelöst und das Formular danach abgeschickt.
 */
export class TurnstileFormGuard {
    constructor(banner) {
        this.banner = banner;
        this.config = (banner?.config || window.pixelmateConfig || {}).turnstile || {};

        this.scriptPromise = null;
        this.widgetId = null;
        this.widgetContainer = null;
        this.overlayEl = null;
        this.modalEl = null;
        this.statusEl = null;

        this.pendingForm = null;
        this.pendingSubmitter = null;

        // Fokus-Rückgabe und Escape-Handling für das Modal (Barrierefreiheit).
        this.lastFocused = null;
        this.keydownHandler = (e) => this.handleKeydown(e);

        if (!this.config.enabled || !this.config.siteKey || !Array.isArray(this.config.selectors) || this.config.selectors.length === 0) {
            this.debug('inactive - config missing/incomplete', this.config);
            return;
        }

        this.selector = this.config.selectors.join(', ');
        this.debug('active, watching selector', this.selector);
        this.init();
    }

    // Loggt nur, wenn window.pixelmateConfig.debug true ist (WP_DEBUG) oder
    // ?pixelmate_turnstile_debug=1 in der URL steht - damit lässt sich das
    // Verhalten live in der Konsole nachvollziehen, ohne die Konsole für
    // alle Besucher vollzuschreiben.
    debug(...args) {
        const forced = typeof window !== 'undefined' && window.location && window.location.search.indexOf('pixelmate_turnstile_debug=1') !== -1;
        if (forced || (window.pixelmateConfig && window.pixelmateConfig.debug)) {
            console.log('[PixelmateTurnstile]', ...args);
        }
    }

    init() {
        // Capture-Phase: fängt den Submit ab, bevor Formular-Plugins (CF7,
        // WPForms, Gravity Forms, ...) ihn per AJAX weiterverarbeiten.
        document.addEventListener('submit', (e) => this.handleSubmit(e), true);
    }

    handleSubmit(e) {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) {
            this.debug('submit ignored - target is not a form', e.target);
            return;
        }
        if (!this.matchesSelector(form)) {
            this.debug('submit ignored - form does not match selector', this.selector, form);
            return;
        }

        // Formular wurde soeben mit gültigem Token erneut abgeschickt - durchlassen.
        if (form.dataset.pixelmateTurnstileVerified === '1') {
            this.debug('submit passed through - already verified', form);
            delete form.dataset.pixelmateTurnstileVerified;
            return;
        }

        this.debug('intercepting submit, opening modal', form);
        e.preventDefault();
        e.stopImmediatePropagation();

        this.pendingForm = form;
        this.pendingSubmitter = e.submitter || null;
        this.openModal();
    }

    matchesSelector(form) {
        try {
            return form.matches(this.selector);
        } catch (err) {
            return false;
        }
    }

    // ==================== MODAL ====================

    openModal() {
        if (!this.modalEl) {
            this.buildModal();
        }

        // Aktuellen Fokus merken, um ihn beim Schließen zurückzugeben.
        this.lastFocused = document.activeElement;

        this.setStatus('intro');
        document.body.appendChild(this.overlayEl);
        document.body.appendChild(this.modalEl);

        document.addEventListener('keydown', this.keydownHandler, true);

        requestAnimationFrame(() => {
            this.overlayEl.style.opacity = '1';
            this.modalEl.style.opacity = '1';
            this.modalEl.style.transform = 'translate(-50%, -50%) scale(1)';

            // Fokus in den Dialog verlegen (Barrierefreiheit / Tastaturbedienung).
            const confirmBtn = this.modalEl.querySelector('[data-action="confirm"]');
            if (confirmBtn) {
                confirmBtn.focus();
            }
        });
    }

    closeModal() {
        if (!this.modalEl) return;

        document.removeEventListener('keydown', this.keydownHandler, true);

        this.overlayEl.style.opacity = '0';
        this.modalEl.style.opacity = '0';
        this.modalEl.style.transform = 'translate(-50%, -50%) scale(0.95)';

        window.setTimeout(() => {
            this.overlayEl?.remove();
            this.modalEl?.remove();
        }, 200);

        // Fokus auf das vorherige Element zurückgeben.
        if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
            this.lastFocused.focus();
        }
        this.lastFocused = null;
    }

    handleKeydown(e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
            e.preventDefault();
            this.cancel();
        }
    }

    // Das öffentliche Stylesheet pixelmate.css wird im Frontend nicht geladen
    // (siehe PixelmateBanner.js) - das Modal stylt sich deshalb wie der Rest
    // des Plugins vollständig über Inline-Styles, statt sich auf die
    // gleichnamigen CSS-Klassen im (ungeladenen) Stylesheet zu verlassen.
    buildModal() {
        const overlay = document.createElement('div');
        overlay.className = 'pixelmate-turnstile-modal-overlay';
        overlay.style.cssText = `
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0, 0, 0, 0.6); z-index: 2147483001;
            opacity: 0; transition: opacity 0.2s ease;
        `;

        const modal = document.createElement('div');
        modal.className = 'pixelmate-turnstile-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('aria-labelledby', 'pixelmate-turnstile-modal-title');
        modal.style.cssText = `
            position: fixed; top: 50%; left: 50%;
            transform: translate(-50%, -50%) scale(0.95);
            width: 90%; max-width: 420px; background: #ffffff; color: #333333;
            border-radius: 8px; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
            padding: 24px; z-index: 2147483002; opacity: 0;
            transition: opacity 0.2s ease, transform 0.2s ease; box-sizing: border-box;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
        `;

        modal.innerHTML = `
            <h3 id="pixelmate-turnstile-modal-title" class="pixelmate-turnstile-modal-title" style="margin: 0 0 12px 0; font-size: 18px; font-weight: 600; line-height: 1.3;">${this.text('title')}</h3>
            <p class="pixelmate-turnstile-modal-text" style="margin: 0 0 16px 0; font-size: 14px; line-height: 1.5;">${this.text('intro')}</p>
            <div class="pixelmate-turnstile-modal-status" style="font-size: 13px; color: #666666; min-height: 18px; margin-bottom: 8px;"></div>
            <div class="pixelmate-turnstile-modal-widget" style="display: flex; justify-content: center; margin-bottom: 8px;"></div>
            <div class="pixelmate-turnstile-modal-actions" style="display: flex; justify-content: flex-end; gap: 12px; margin-top: 8px;">
                <button type="button" class="pixelmate-turnstile-modal-btn pixelmate-turnstile-modal-btn-secondary" data-action="cancel" style="border: none; padding: 10px 18px; font-size: 14px; font-weight: 600; border-radius: 4px; cursor: pointer; white-space: nowrap; background: transparent; color: #666666;">${this.text('cancel')}</button>
                <button type="button" class="pixelmate-turnstile-modal-btn pixelmate-turnstile-modal-btn-primary" data-action="confirm" style="border: none; padding: 10px 18px; font-size: 14px; font-weight: 600; border-radius: 4px; cursor: pointer; white-space: nowrap; background: #0073aa; color: #ffffff;">${this.text('confirm')}</button>
            </div>
        `;

        modal.querySelector('[data-action="cancel"]').addEventListener('click', () => this.cancel());
        modal.querySelector('[data-action="confirm"]').addEventListener('click', () => this.onConfirm());
        overlay.addEventListener('click', () => this.cancel());

        this.overlayEl = overlay;
        this.modalEl = modal;
        this.statusEl = modal.querySelector('.pixelmate-turnstile-modal-status');
        this.widgetContainer = modal.querySelector('.pixelmate-turnstile-modal-widget');
    }

    text(key) {
        // Deutsche Standardtexte als Fallback. Die tatsächlich angezeigten Texte
        // liefert die Serverseite übersetzt via config.texts (SettingsLocalizer),
        // damit das Modal der WordPress-Sprache folgt statt immer Deutsch zu sein.
        const defaults = {
            title: 'Sicherheitsprüfung erforderlich',
            intro: 'Zum Schutz dieses Formulars vor Spam und automatisierten Zugriffen muss eine Verbindung zu Cloudflare (Turnstile) aufgebaut werden. Dabei wird deine IP-Adresse zur Sicherheitsprüfung an Cloudflare übermittelt.',
            confirm: 'Sicherheitsprüfung starten',
            cancel: 'Abbrechen',
            loading: 'Verbindung zu Cloudflare wird aufgebaut …',
            error: 'Die Sicherheitsprüfung konnte nicht geladen werden. Bitte versuche es erneut.',
            retry: 'Erneut versuchen',
        };
        const texts = this.config.texts || {};
        return texts[key] || defaults[key] || '';
    }

    setStatus(state) {
        if (!this.statusEl) return;

        const confirmBtn = this.modalEl.querySelector('[data-action="confirm"]');
        const cancelBtn = this.modalEl.querySelector('[data-action="cancel"]');

        if (state === 'intro') {
            this.statusEl.textContent = '';
            this.widgetContainer.innerHTML = '';
            confirmBtn.style.display = '';
            confirmBtn.disabled = false;
            confirmBtn.textContent = this.text('confirm');
            cancelBtn.style.display = '';
        } else if (state === 'loading') {
            this.statusEl.textContent = this.text('loading');
            confirmBtn.style.display = 'none';
        } else if (state === 'widget') {
            this.statusEl.textContent = '';
            confirmBtn.style.display = 'none';
        } else if (state === 'error') {
            this.statusEl.textContent = this.text('error');
            confirmBtn.style.display = '';
            confirmBtn.disabled = false;
            confirmBtn.textContent = this.text('retry');
            cancelBtn.style.display = '';
        }
    }

    cancel() {
        this.pendingForm = null;
        this.pendingSubmitter = null;
        this.closeModal();
    }

    onConfirm() {
        this.debug('modal confirmed, loading challenges.cloudflare.com/turnstile/v0/api.js');
        this.setStatus('loading');

        this.loadScript()
            .then(() => this.renderWidget())
            .catch((err) => {
                this.debug('failed to load Turnstile script', err);
                this.setStatus('error');
            });
    }

    // ==================== TURNSTILE ====================

    loadScript() {
        if (window.turnstile) {
            return Promise.resolve();
        }

        if (this.scriptPromise) {
            return this.scriptPromise;
        }

        this.scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
            script.async = true;
            script.defer = true;
            script.addEventListener('load', () => resolve());
            script.addEventListener('error', () => {
                this.scriptPromise = null;
                reject(new Error('Turnstile script failed to load'));
            });
            document.head.appendChild(script);
        });

        return this.scriptPromise;
    }

    renderWidget() {
        if (!window.turnstile || !this.widgetContainer) {
            this.setStatus('error');
            return;
        }

        this.setStatus('widget');
        this.widgetContainer.innerHTML = '';

        this.widgetId = window.turnstile.render(this.widgetContainer, {
            sitekey: this.config.siteKey,
            theme: this.config.theme || 'auto',
            size: this.config.size || 'normal',
            appearance: this.config.appearance || 'always',
            callback: (token) => this.onToken(token),
            'error-callback': () => this.setStatus('error'),
            'expired-callback': () => {
                if (this.widgetId !== null) {
                    window.turnstile.reset(this.widgetId);
                }
            },
        });
    }

    onToken(token) {
        const form = this.pendingForm;
        const submitter = this.pendingSubmitter;

        if (!form) return;

        this.debug('token received, re-submitting form', form);
        this.setTokenOnForm(form, token);
        this.pendingForm = null;
        this.pendingSubmitter = null;
        this.closeModal();

        form.dataset.pixelmateTurnstileVerified = '1';

        if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter || undefined);
        } else {
            form.submit();
        }
    }

    setTokenOnForm(form, token) {
        let input = form.querySelector('input[name="cf-turnstile-response"]');
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'cf-turnstile-response';
            form.appendChild(input);
        }
        input.value = token;
    }
}
